<script>
import ENUMS from '@shared/messages/enums.json';
import { expandRecurringCalendarEvents } from '@shared/utils/calendar';

export default {
  props: {
    events: {
      type: Array,
      default: () => [],
    },
    month: {
      type: Number,
      required: true,
    },
    year: {
      type: Number,
      required: true,
    },
    selectedDay: {
      type: String,
      default: null,
    },
  },
  i18n: {
    messages: {
      en: {
        today: 'Today',
      },
      fr: {
        today: "Aujourd'hui",
      },
    },
  },
  computed: {
    monthLabel() {
      return this.$moment().month(this.month).year(this.year).format('MMMM YYYY');
    },
    daysOfWeek() {
      const days = [];
      for (let i = 1; i <= 7; i++) {
        days.push(this.$moment().isoWeekday(i).format('ddd'));
      }
      return days;
    },
    gridRange() {
      const start = this.$moment().year(this.year).month(this.month).startOf('month');
      const end = this.$moment().year(this.year).month(this.month).endOf('month');
      const gridStart = start.clone().subtract(start.isoWeekday() - 1, 'days');
      // +1: diff() truncates (ex: august 2026 = 35.99 → 35), which cut the 6th row
      const totalDays = Math.ceil((end.diff(gridStart, 'days') + 1) / 7) * 7;
      const gridEnd = gridStart.clone().add(totalDays - 1, 'days');
      return {
        start: gridStart.format('YYYY-MM-DD'),
        end: gridEnd.format('YYYY-MM-DD'),
      };
    },
    expandedEvents() {
      return expandRecurringCalendarEvents(this.events, this.gridRange.start, this.gridRange.end);
    },
    calendarDays() {
      const start = this.$moment().year(this.year).month(this.month).startOf('month');
      const end = this.$moment().year(this.year).month(this.month).endOf('month');
      const startDay = start.isoWeekday() - 1;

      const days = [];

      // Leading empty days
      const prevMonth = start.clone().subtract(1, 'day');
      for (let i = startDay - 1; i >= 0; i--) {
        days.push({
          date: prevMonth.clone().subtract(i, 'days'),
          day: prevMonth.clone().subtract(i, 'days').date(),
          isOutside: true,
          events: [],
        });
      }

      // Actual days
      const current = start.clone();
      while (current.isSameOrBefore(end, 'day')) {
        const dateStr = current.format('YYYY-MM-DD');
        const dayEvents = this.expandedEvents.filter((e) => {
          const tz = e.timezone || 'Europe/Paris';
          const eventStart = this.$moment.utc(e.start_date).tz(tz).format('YYYY-MM-DD');
          const eventEnd = e.end_date
            ? this.$moment.utc(e.end_date).tz(tz).format('YYYY-MM-DD')
            : eventStart;
          return dateStr >= eventStart && dateStr <= eventEnd;
        });
        days.push({
          date: current.clone(),
          dateStr,
          day: current.date(),
          isToday: current.isSame(this.$moment(), 'day'),
          isPast: current.isBefore(this.$moment(), 'day'),
          isOutside: false,
          events: dayEvents,
        });
        current.add(1, 'day');
      }

      // Trailing empty days
      let nextDay = 1;
      while (days.length % 7 !== 0) {
        days.push({
          date: end.clone().add(nextDay, 'days'),
          day: nextDay,
          isOutside: true,
          events: [],
        });
        nextDay++;
      }

      return days;
    },
    calendarWeeks() {
      const weeks = [];
      for (let i = 0; i < this.calendarDays.length; i += 7) {
        weeks.push(this.calendarDays.slice(i, i + 7));
      }
      return weeks;
    },
  },
  data() {
    return {
      dayModal: {
        active: false,
        cell: null,
      },
    };
  },
  methods: {
    prevMonth() {
      let m = this.month - 1;
      let y = this.year;
      if (m < 0) {
        m = 11;
        y -= 1;
      }
      this.$emit('month-change', { month: m, year: y });
    },
    nextMonth() {
      let m = this.month + 1;
      let y = this.year;
      if (m > 11) {
        m = 0;
        y += 1;
      }
      this.$emit('month-change', { month: m, year: y });
    },
    goToToday() {
      const now = this.$moment();
      this.$emit('month-change', { month: now.month(), year: now.year() });
    },
    onEventClick(event) {
      this.$emit('event-click', event);
    },
    onDayClick(cell) {
      if (!cell.isOutside) {
        this.$emit('day-click', cell);
      }
    },
    openDayModal(cell) {
      this.dayModal = { active: true, cell };
    },
    onModalEventClick(event) {
      this.dayModal.active = false;
      this.$emit('event-click', event);
    },
    dayModalTitle() {
      if (!this.dayModal.cell) return '';
      return this.$moment(this.dayModal.cell.dateStr).format('dddd DD MMMM YYYY');
    },
    statusClass(event) {
      return ENUMS.calendarEventStatusClass[event.status] || null;
    },
    formatTime(event, cellDateStr) {
      if (event.is_all_day) return '';
      const tz = event.timezone || 'Europe/Paris';
      const start = this.$moment.utc(event.start_date).tz(tz);

      // Jour de continuation d'un événement multi-jours : la journée est couverte
      // depuis minuit, elle n'a pas d'heure de début propre → pas d'heure affichée
      // (au lieu de répéter l'heure de début sur chaque case).
      if (cellDateStr && event.end_date) {
        const end = this.$moment.utc(event.end_date).tz(tz);
        if (!start.isSame(end, 'day') && cellDateStr !== start.format('YYYY-MM-DD')) {
          return '';
        }
      }

      // Un début à minuit n'apporte rien dans la grille (équivalent « journée
      // entière ») : on n'affiche que les heures significatives.
      if (start.hour() === 0 && start.minute() === 0) {
        return '';
      }

      return start.format('HH:mm');
    },
  },
};
</script>

<template>
  <div class="compcg">
    <!-- Header -->
    <div class="is-flex is-align-items-center is-justify-content-space-between mb-4">
      <div>
        <p class="has-text-weight-bold is-size-5 is-capitalized">
          {{ monthLabel }}
        </p>
      </div>
      <div class="is-flex is-align-items-center" style="gap: 8px;">
        <b-button icon-left="chevron-left" @click="prevMonth" />
        <b-button @click="goToToday">
          {{ $t('today') }}
        </b-button>
        <b-button icon-left="chevron-right" @click="nextMonth" />
      </div>
    </div>

    <!-- Grid -->
    <div class="box overflow-hidden p-0">
      <div class="table-container">
        <table class="compcg_table table is-bordered is-fullwidth mb-0">
        <thead>
          <tr class="compcg_header">
            <th
              v-for="day in daysOfWeek"
              :key="day"
              class="compcg_header_cell has-text-centered is-size-9 has-text-weight-medium is-uppercase"
            >
              {{ day }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(week, wi) in calendarWeeks"
            :key="wi"
            class="compcg_row"
          >
            <td
              v-for="(cell, di) in week"
              :key="di"
              class="compcg_cell"
              :class="{
                'is-outside': cell.isOutside,
                'is-past': cell.isPast,
                'is-today': cell.isToday,
                'is-selected': cell.dateStr === selectedDay,
              }"
              @click="onDayClick(cell)"
            >
              <span class="compcg_day" :class="{ 'compcg_today': cell.isToday }">
                {{ cell.day }}
              </span>
              <div class="compcg_events">
                <div
                  v-for="ev in cell.events.slice(0, 3)"
                  :key="ev.uuid"
                  class="compcg_event"
                  :class="{ 'compcg_event--item': ev.type === 'training_item' }"
                  @click.stop="onEventClick(ev)"
                >
                  <b-icon
                    v-if="statusClass(ev)"
                    icon="circle"
                    :type="statusClass(ev)"
                    class="compcg_event_dot"
                  />
                  <b-icon
                    v-if="ev.location_type === 'talkizy'"
                    icon="video"
                    class="compcg_event_cam"
                  />
                  <span v-if="formatTime(ev, cell.dateStr)" class="compcg_event_time">{{ formatTime(ev, cell.dateStr) }}</span>
                  <span class="compcg_event_title">{{ ev.title }}</span>
                </div>
                <div
                  v-if="cell.events.length > 3"
                  class="compcg_event compcg_more"
                  @click.stop="openDayModal(cell)"
                >
                  <span class="compcg_event_title">+{{ cell.events.length - 3 }}</span>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      </div>
    </div>

    <!-- All events of a day -->
    <b-modal v-model="dayModal.active" :width="420" scroll="keep">
      <div v-if="dayModal.cell" class="modal-card" style="width: auto">
        <header class="modal-card-head">
          <p class="modal-card-title is-size-6 is-capitalized">
            {{ dayModalTitle() }}
          </p>
          <button type="button" class="delete" aria-label="close" @click="dayModal.active = false" />
        </header>
        <section class="modal-card-body">
          <div
            v-for="ev in dayModal.cell.events"
            :key="`${ev.uuid}-${ev.start_date}`"
            class="compcg_event compcg_modalevent"
            :class="{ 'compcg_event--item': ev.type === 'training_item' }"
            @click="onModalEventClick(ev)"
          >
            <b-icon
              v-if="statusClass(ev)"
              icon="circle"
              :type="statusClass(ev)"
              class="compcg_event_dot"
            />
            <b-icon
              v-if="ev.location_type === 'talkizy'"
              icon="video"
              class="compcg_event_cam"
            />
            <span v-if="formatTime(ev, dayModal.cell.dateStr)" class="compcg_event_time">{{ formatTime(ev, dayModal.cell.dateStr) }}</span>
            <span class="compcg_event_title">{{ ev.title }}</span>
          </div>
        </section>
        <footer class="modal-card-foot" />
      </div>
    </b-modal>
  </div>
</template>

<style lang="scss" scoped>
.compcg_table {
  table-layout: fixed;

  // The box is the outer frame: strip the table's outside borders
  thead th {
    border-top: 0;
  }

  th:first-child,
  td:first-child {
    border-left: 0;
  }

  th:last-child,
  td:last-child {
    border-right: 0;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }
}

.compcg_header_cell {
  padding-top: 12px;
  padding-bottom: 12px;
  color: $theme_color_black_30;
}

.compcg_cell {
  height: 90px;

  &.is-outside {
    background-color: $theme_color_grey_light2;

    .compcg_day {
      color: $theme_color_black_10;
    }
  }

  // Jours déjà passés du mois : numéro barré et contenu atténué, pour distinguer
  // d'un coup d'œil le passé du présent/à venir (aujourd'hui et futur inchangés).
  &.is-past {
    .compcg_day {
      color: $theme_color_black_30;
      text-decoration: line-through;
    }

    .compcg_events {
      opacity: 0.5;
    }
  }

  &.is-today {
    background-color: $theme_color_primary_light;
  }

  &.is-selected {
    background-color: $theme_color_primary_light;
  }
}

.compcg_day {
  font-size: $size-8;
  font-weight: $theme_fontWeight_500;
  color: $theme_color_black_70;
  display: inline-block;
  margin-bottom: 2px;
}

.compcg_today {
  background-color: var(--brand-color, #{$primary});
  color: $theme_color_white;
  border-radius: $theme_radius_4;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: $theme_fontWeight_700;
}

.compcg_events {
  display: flex;
  flex-direction: column;
  gap: 2px;
  // Élargit légèrement chaque ligne en débordant sur le padding de la cellule,
  // pour gagner un peu de place au texte sans décaler le numéro du jour.
  margin: 0 -8px;
}

.compcg_event {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 1px 6px;
  border-radius: $theme_radius_4;
  background: $theme_color_primary_light;
  cursor: pointer;
  overflow: hidden;

  &:hover {
    background: darken($theme_color_primary_light, 5%);
  }

  &--item {
    background: $theme_color_white;
    border: 1px solid var(--brand-color, #{$primary});

    &:hover {
      background: $theme_color_grey_light2;
    }
  }
}

.compcg_event_dot {
  width: auto;
  height: auto;
  font-size: 7px;
  flex-shrink: 0;
}

// Petite caméra signalant une classe virtuelle Talkizy, aux couleurs du store.
.compcg_event_cam {
  width: auto;
  height: auto;
  font-size: 10px;
  flex-shrink: 0;
  color: var(--brand-color, #{$primary});
}

.compcg_event_time {
  font-size: $size-9;
  color: $theme_color_black_70;
  flex-shrink: 0;
}

.compcg_event_title {
  font-size: $size-9;
  font-weight: $theme_fontWeight_500;
  color: $theme_color_third;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .compcg_event--item & {
    color: $theme_color_black;
  }
}

.compcg_more {
  .compcg_event_title {
    color: $theme_color_black_70;
  }
}

.compcg_modalevent {
  padding: 6px 8px;

  & + & {
    margin-top: 6px;
  }
}
</style>
